import type { AgencyClientSummary } from "./types";

export type AgencyClientListSortDirection = "asc" | "desc";

export type AgencyClientListVisibleSortField =
  | "name"
  | "slug"
  | "planName"
  | "planStatus";

export type AgencyClientListServerSortColumn = "name" | "slug" | "id";

export interface AgencyClientListSort {
  field: AgencyClientListVisibleSortField;
  direction: AgencyClientListSortDirection;
}

export interface AgencyClientListOrderClause {
  column: AgencyClientListServerSortColumn;
  ascending: boolean;
  nullsFirst: boolean;
}

export const DEFAULT_AGENCY_CLIENT_LIST_SORT: AgencyClientListSort = {
  field: "name",
  direction: "asc",
};

const DIRECT_ORG_FIELDS = new Set<AgencyClientListVisibleSortField>([
  "name",
  "slug",
]);

const STABLE_ORG_ORDER: AgencyClientListServerSortColumn[] = [
  "name",
  "slug",
  "id",
];

export function defaultAgencyClientListSortDirection(
  _field: AgencyClientListVisibleSortField,
): AgencyClientListSortDirection {
  return "asc";
}

export function nextAgencyClientListSort(
  current: AgencyClientListSort,
  field: AgencyClientListVisibleSortField,
): AgencyClientListSort {
  if (current.field !== field) {
    return { field, direction: defaultAgencyClientListSortDirection(field) };
  }

  return {
    field,
    direction: current.direction === "asc" ? "desc" : "asc",
  };
}

export function isAgencyClientJoinedSort(sort: AgencyClientListSort): boolean {
  return !DIRECT_ORG_FIELDS.has(sort.field);
}

export function getAgencyClientOrgOrderClauses(
  sort: AgencyClientListSort = DEFAULT_AGENCY_CLIENT_LIST_SORT,
): AgencyClientListOrderClause[] {
  const clauses: AgencyClientListOrderClause[] = [];

  if (sort.field === "name" || sort.field === "slug") {
    clauses.push({
      column: sort.field,
      ascending: sort.direction === "asc",
      nullsFirst: false,
    });
  }

  for (const column of STABLE_ORG_ORDER) {
    if (clauses.some((clause) => clause.column === column)) continue;
    clauses.push({ column, ascending: true, nullsFirst: false });
  }

  return clauses;
}

type SortableAgencyClient = Pick<
  AgencyClientSummary,
  "org" | "planName" | "planStatus"
>;

function normalizedText(value: string | null | undefined): string | null {
  const text = value?.trim().toLowerCase() ?? "";
  return text || null;
}

function compareNullableText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: AgencyClientListSortDirection,
): number {
  const aText = normalizedText(a);
  const bText = normalizedText(b);
  if (!aText && !bText) return 0;
  if (!aText) return 1;
  if (!bText) return -1;
  const comparison = aText.localeCompare(bText);
  return direction === "asc" ? comparison : -comparison;
}

function planStatusSortValue(
  status: AgencyClientSummary["planStatus"],
): string {
  if (!status) return "No plan";
  return status;
}

function compareAgencyClientField(
  a: SortableAgencyClient,
  b: SortableAgencyClient,
  sort: AgencyClientListSort,
): number {
  switch (sort.field) {
    case "name":
      return compareNullableText(a.org.name, b.org.name, sort.direction);
    case "slug":
      return compareNullableText(a.org.slug, b.org.slug, sort.direction);
    case "planName":
      return compareNullableText(a.planName, b.planName, sort.direction);
    case "planStatus":
      return compareNullableText(
        planStatusSortValue(a.planStatus),
        planStatusSortValue(b.planStatus),
        sort.direction,
      );
  }
}

export function sortAgencyClients<T extends SortableAgencyClient>(
  clients: readonly T[],
  sort: AgencyClientListSort = DEFAULT_AGENCY_CLIENT_LIST_SORT,
): T[] {
  return clients
    .map((client, index) => ({ client, index }))
    .sort((a, b) => {
      const primary = compareAgencyClientField(a.client, b.client, sort);
      if (primary !== 0) return primary;

      const nameTieBreak = compareNullableText(
        a.client.org.name,
        b.client.org.name,
        "asc",
      );
      if (nameTieBreak !== 0) return nameTieBreak;

      const slugTieBreak = compareNullableText(
        a.client.org.slug,
        b.client.org.slug,
        "asc",
      );
      if (slugTieBreak !== 0) return slugTieBreak;

      const idTieBreak = compareNullableText(a.client.org.id, b.client.org.id, "asc");
      if (idTieBreak !== 0) return idTieBreak;

      return a.index - b.index;
    })
    .map(({ client }) => client);
}
